import { Injectable } from "@angular/core";
import { DecisionAction, DecisionActionCompact, DecisionActivity, DecisionEvidence } from "../../models/home";
import { LanguageService } from "../language/language.service";
import { HomeDecisionActionI18n } from "./home-decision-detail-i18n.service.action-copy";
import { HomeDecisionEvidenceHistoryI18n } from "./home-decision-detail-i18n.service.evidence-and-history";
import { HomeDecisionI18nCore } from "./home-decision-detail-i18n.service.technical-and-fallbacks";
import { HomeDecisionDetailTechnicalI18nService } from "./home-decision-detail-technical-i18n.service";

type ActionLike = Pick<DecisionActionCompact, "actionKey" | "label"> & Partial<Pick<DecisionAction, "description">>;

@Injectable({ providedIn: "root" })
export class HomeDecisionDetailI18nService {
  private readonly core: HomeDecisionI18nCore;
  private readonly actions: HomeDecisionActionI18n;
  private readonly evidenceHistory: HomeDecisionEvidenceHistoryI18n;

  constructor(lang: LanguageService, technicalI18n: HomeDecisionDetailTechnicalI18nService) {
    this.core = new HomeDecisionI18nCore(lang, technicalI18n);
    this.actions = new HomeDecisionActionI18n(lang, this.core);
    this.evidenceHistory = new HomeDecisionEvidenceHistoryI18n(lang, technicalI18n, this.core);
  }

  translateDecisionReason(reason?: string | null): string | null { return this.actions.translateDecisionReason(reason); }
  translateActionLabel(action: ActionLike | null | undefined): string | null { return this.actions.translateActionLabel(action); }
  translateActionDescription(action: ActionLike | null | undefined): string | null { return this.actions.translateActionDescription(action); }
  translateActionSummaryLabel(summary: string | null | undefined, action: ActionLike | null | undefined): string {
    return this.actions.translateActionSummaryLabel(summary, action);
  }
  translateActionSummaryCopy(summary: string | null | undefined, action: ActionLike | null | undefined): string {
    return this.actions.translateActionSummaryCopy(summary, action);
  }
  translateActionCriterion(action: ActionLike | null | undefined, criterion: string | null | undefined, index: number): string {
    return this.actions.translateActionCriterion(action, criterion, index);
  }
  translateActionDisabledReason(reason?: string | null): string | null { return this.actions.translateActionDisabledReason(reason); }
  translateActionFieldOptionLabel(rawLabel?: string | null): string | null { return this.actions.translateActionFieldOptionLabel(rawLabel); }
  translateActionSubmitLabel(action: ActionLike | null | undefined): string { return this.actions.translateActionSubmitLabel(action); }
  translateActionFormCopy(action: ActionLike | null | undefined): string | null { return this.actions.translateActionFormCopy(action); }
  translateActionConfirmationLabel(action: ActionLike | null | undefined): string { return this.actions.translateActionConfirmationLabel(action); }

  translateActionFieldLabel(actionKey: string | null | undefined, fieldKey: string, rawLabel?: string | null): string {
    return this.translateActionFieldKey(actionKey, fieldKey, "label") ?? this.translateFieldKey(fieldKey, "label") ??
      this.translateCopy(rawLabel) ?? rawLabel ?? this.humanizeKey(fieldKey);
  }
  translateActionFieldPlaceholder(actionKey: string | null | undefined, fieldKey: string, raw?: string | null): string | null {
    return this.translateActionFieldKey(actionKey, fieldKey, "placeholder") ?? this.translateFieldKey(fieldKey, "placeholder") ??
      this.translateCopy(raw) ?? raw ?? null;
  }
  translateActionFieldHelperText(actionKey: string | null | undefined, fieldKey: string, raw?: string | null): string | null {
    return this.translateActionFieldKey(actionKey, fieldKey, "helper") ?? this.translateFieldKey(fieldKey, "helper") ??
      this.translateCopy(raw) ?? raw ?? null;
  }

  translateTechnicalValue(key: string, value: unknown): string { return this.core.translateTechnicalValue(key, value); }
  isTechnicalValueMultiline(value: unknown): boolean { return this.core.isTechnicalValueMultiline(value); }
  translateFieldKey(key: string, suffix: "label" | "placeholder" | "helper"): string | null { return this.core.translateFieldKey(key, suffix); }
  translateState(state?: string | null): string | null { return this.core.translateState(state); }
  translateDateEvidenceLabel(label?: string | null): string | null { return this.core.translateDateEvidenceLabel(label); }
  translateEvidenceLabelToken(label?: string | null): string | null { return this.core.translateEvidenceLabelToken(label); }
  translateCopy(raw?: string | null): string | null { return this.core.translateCopy(raw); }
  translateDynamicCopy(raw?: string | null): string | null { return this.core.translateDynamicCopy(raw); }
  translateByNormalizedKey(value: string | null | undefined, prefix: string): string | null { return this.core.translateByNormalizedKey(value, prefix); }
  translateKnownKey(key: string): string | null { return this.core.translateKnownKey(key); }
  interpolate(key: string, values: Record<string, string>): string | null { return this.core.interpolate(key, values); }
  resolveActorLabel(activity: DecisionActivity): string { return this.core.resolveActorLabel(activity); }
  shouldTranslateResponses(): boolean { return this.core.shouldTranslateResponses(); }
  normalizeKey(value?: string | null): string | null { return this.core.normalizeKey(value); }
  humanizeKey(value: string): string { return this.core.humanizeKey(value); }
  translateEvidenceLabel(evidence: DecisionEvidence): string { return this.evidenceHistory.translateEvidenceLabel(evidence); }
  translateEvidenceValue(evidence: DecisionEvidence): string { return this.evidenceHistory.translateEvidenceValue(evidence); }
  translateMissingInformationReason(reason?: string | null): string | null { return this.evidenceHistory.translateMissingInformationReason(reason); }
  translateHistoryTitle(activity: DecisionActivity): string { return this.evidenceHistory.translateHistoryTitle(activity); }
  translateHistoryNarrative(activity: DecisionActivity): string { return this.evidenceHistory.translateHistoryNarrative(activity); }

  private translateActionFieldKey(actionKey: string | null | undefined, fieldKey: string, suffix: "label" | "placeholder" | "helper"): string | null {
    const action = this.normalizeKey(actionKey);
    const field = this.normalizeKey(fieldKey);
    return action && field
      ? this.translateKnownKey(`home.decisionDetail.action.${action}.field.${field}.${suffix}`)
      : null;
  }
}
